import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Megaphone, Tag, HandHelping, Coins, ShieldAlert, ImagePlus, Video, X, Play } from "lucide-react";
import { Layout } from "@/components/Layout";
import { PostCard } from "@/components/PostCard";
import { PlansModal } from "@/components/PlansModal";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api, mediaUrl, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { compressImage } from "@/lib/compressImage";

export default function Feed() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [business, setBusiness] = useState(null);
  const [type, setType] = useState("Offer");
  const [content, setContent] = useState("");
  const [posting, setPosting] = useState(false);
  const [plansOpen, setPlansOpen] = useState(false);
  const [scope, setScope] = useState("all");
  const [media, setMedia] = useState([]);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const photoRef = useRef(null);
  const videoRef = useRef(null);

  const fetchPosts = useCallback(async (f, sc) => {
    setLoading(true);
    try {
      if (sc === "following") {
        const { data } = await api.get("/feed");
        setPosts(f !== "all" ? data.filter((p) => p.type === f) : data);
      } else {
        const { data } = await api.get("/posts", { params: { type: f !== "all" ? f : undefined } });
        setPosts(data);
      }
    } catch (e) { setPosts([]); } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchPosts(filter, scope); }, [filter, scope, fetchPosts]);

  useEffect(() => {
    if (user) api.get("/businesses/me").then(({ data }) => setBusiness(data)).catch((e) => console.error("Failed to load my business", e));
  }, [user]);

  const canPost = user && business && business.gst_verified;
  const noCredits = user && (user.credits ?? 0) <= 0;
  const hasVideo = media.some((m) => m.media_type === "video");
  const photoCount = media.filter((m) => m.media_type === "photo").length;

  const uploadFiles = async (files, mediaType) => {
    if (!files || files.length === 0) return;
    const list = Array.from(files);
    if (mediaType === "video") {
      if (media.length > 0) { toast.error("Attach either up to 4 photos or 1 video"); return; }
    } else if (hasVideo || photoCount + list.length > 4) {
      toast.error("You can attach up to 4 photos (or 1 video)"); return;
    }
    setUploadingMedia(true);
    try {
      for (const file of (mediaType === "video" ? [list[0]] : list.slice(0, 4 - photoCount))) {
        const fd = new FormData();
        fd.append("file", mediaType === "photo" ? await compressImage(file) : file);
        fd.append("media_type", mediaType);
        const { data } = await api.post("/posts/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setMedia((m) => [...m, { url: data.url, media_type: data.media_type }]);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally { setUploadingMedia(false); }
  };

  const removeMedia = (i) => setMedia((m) => m.filter((_, idx) => idx !== i));

  const submitPost = async (e) => {
    e.preventDefault();
    if (!content.trim() && media.length === 0) return;
    setPosting(true);
    try {
      const { data } = await api.post("/posts", { type, content, media });
      setPosts((p) => [data.post, ...p]);
      setContent("");
      setMedia([]);
      setUser({ ...user, credits: data.credits });
      toast.success(`Posted! ${data.credits} credits left.`);
    } catch (err) {
      const status = err.response?.status;
      if (status === 402) {
        toast.error("You're out of credits.");
        setPlansOpen(true);
      } else {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not post");
      }
    } finally {
      setPosting(false);
    }
  };

  return (
    <Layout>
      <div className="mx-auto max-w-2xl px-5 sm:px-8 py-10">
        <div className="flex items-center gap-3 mb-1">
          <Megaphone className="text-brand" size={24} />
          <h1 className="font-display font-black tracking-tight text-3xl sm:text-4xl text-slate-900">Business Feed</h1>
        </div>
        <p className="text-slate-500 mb-8">Offers and requirements from verified businesses across India.</p>

        {/* Composer */}
        {user && (
          <div className="mb-8">
            {canPost ? (
              <form onSubmit={submitPost} data-testid="post-composer" className="bg-white border border-slate-200 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-3">
                  {["Offer", "Requirement", "Update"].map((t) => {
                    const active = type === t;
                    const Icon = t === "Offer" ? Tag : t === "Requirement" ? HandHelping : Megaphone;
                    return (
                      <button key={t} type="button" data-testid={`post-type-${t}`} onClick={() => setType(t)}
                        className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-semibold border transition-colors ${active ? "bg-brand text-white border-brand" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>
                        <Icon size={14} /> {t}
                      </button>
                    );
                  })}
                  <span className="ml-auto inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500">
                    <Coins size={15} className="text-brand" /> {user.credits ?? 0} credits
                  </span>
                </div>
                <Textarea data-testid="post-content-input" value={content} onChange={(e) => setContent(e.target.value)} placeholder={type === "Offer" ? "Share a deal or offer with the network…" : type === "Requirement" ? "Describe what your business needs…" : "Share a business update with your followers…"} className="bg-slate-50 min-h-[90px]" />

                {/* Media previews */}
                {media.length > 0 && (
                  <div data-testid="composer-media-previews" className="mt-3 flex flex-wrap gap-2">
                    {media.map((m, i) => (
                      <div key={i} className="relative h-20 w-20 rounded-lg overflow-hidden bg-slate-100 border border-slate-200">
                        {m.media_type === "video" ? (
                          <><video src={mediaUrl(m.url)} className="h-full w-full object-cover" /><span className="absolute inset-0 flex items-center justify-center bg-black/25"><Play size={18} className="text-white" /></span></>
                        ) : (
                          <img src={mediaUrl(m.url)} alt="" className="h-full w-full object-cover" />
                        )}
                        <button type="button" data-testid={`composer-remove-media-${i}`} onClick={() => removeMedia(i)} className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-red-600"><X size={13} /></button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Attach controls */}
                <div className="mt-3 flex items-center gap-2">
                  <input ref={photoRef} type="file" accept="image/*" multiple className="hidden" data-testid="composer-photo-input" onChange={(e) => { uploadFiles(e.target.files, "photo"); e.target.value = ""; }} />
                  <input ref={videoRef} type="file" accept="video/*" className="hidden" data-testid="composer-video-input" onChange={(e) => { uploadFiles(e.target.files, "video"); e.target.value = ""; }} />
                  <Button type="button" data-testid="composer-add-photo" onClick={() => photoRef.current?.click()} disabled={uploadingMedia || hasVideo || photoCount >= 4} variant="outline" size="sm" className="gap-1.5 border-slate-300 font-semibold text-slate-600">
                    {uploadingMedia ? <Loader2 className="animate-spin" size={15} /> : <ImagePlus size={15} />} Photo ({photoCount}/4)
                  </Button>
                  <Button type="button" data-testid="composer-add-video" onClick={() => videoRef.current?.click()} disabled={uploadingMedia || media.length > 0} variant="outline" size="sm" className="gap-1.5 border-slate-300 font-semibold text-slate-600">
                    <Video size={15} /> Video
                  </Button>
                  <span className="text-[11px] text-slate-400">Up to 4 photos or 1 video</span>
                </div>

                <div className="flex items-center justify-between mt-3">
                  <span className="text-xs text-slate-400">Costs 1 credit per post</span>
                  {noCredits ? (
                    <Button type="button" data-testid="feed-buy-credits-btn" onClick={() => setPlansOpen(true)} className="rounded-lg bg-brand hover:bg-brand-hover font-semibold">Buy Credits</Button>
                  ) : (
                    <Button type="submit" data-testid="post-submit-btn" disabled={posting} className="rounded-lg bg-brand hover:bg-brand-hover font-semibold gap-2">
                      {posting && <Loader2 className="animate-spin" size={16} />} Post Update
                    </Button>
                  )}
                </div>
              </form>
            ) : (
              <div data-testid="post-gate" className="bg-amber-50 border border-amber-200 rounded-xl p-5 flex items-start gap-3">
                <ShieldAlert className="text-amber-600 shrink-0" size={20} />
                <div>
                  <p className="font-semibold text-amber-800">Only GST-verified businesses can post</p>
                  <p className="text-sm text-amber-700 mt-0.5">
                    {business ? "Add a valid GST number to your profile to unlock posting." : "Create your business profile with a valid GST number first."}
                  </p>
                  <Button data-testid="gate-goto-profile" onClick={() => navigate("/dashboard")} variant="outline" size="sm" className="mt-3 border-amber-300 text-amber-800 hover:bg-amber-100">Go to my profile</Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Scope + Filters */}
        {user && (
          <div className="flex items-center gap-2 mb-4">
            {[["all", "All Businesses"], ["following", "Following"]].map(([v, label]) => (
              <button key={v} data-testid={`feed-scope-${v}`} onClick={() => setScope(v)}
                className={`rounded-full px-4 py-1.5 text-sm font-bold border transition-colors ${scope === v ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>
                {label}
              </button>
            ))}
          </div>
        )}
        <div className="flex items-center gap-2 mb-6">
          {[["all", "All"], ["Offer", "Offers"], ["Requirement", "Requirements"], ["Update", "Updates"]].map(([v, label]) => (
            <button key={v} data-testid={`feed-filter-${v}`} onClick={() => setFilter(v)}
              className={`rounded-full px-4 py-1.5 text-sm font-semibold border transition-colors ${filter === v ? "bg-brand text-white border-brand" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>
              {label}
            </button>
          ))}
        </div>

        {/* Posts */}
        {loading ? (
          <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-brand" size={30} /></div>
        ) : posts.length === 0 ? (
          <div data-testid="feed-empty" className="py-16 text-center">
            <Megaphone className="mx-auto text-slate-300 mb-3" size={36} />
            <p className="font-display font-bold text-lg text-slate-700">No posts yet</p>
            <p className="text-slate-500">Verified businesses will share offers and requirements here.</p>
          </div>
        ) : (
          <div data-testid="feed-list" className="space-y-5">
            {posts.map((p) => <PostCard key={p.id} post={p} />)}
          </div>
        )}
      </div>

      <PlansModal open={plansOpen} onOpenChange={setPlansOpen} />
    </Layout>
  );
}
